<template>
    <div class="page-container">
      <div class="card-wrapper">
        <!-- <div class="pageTitle">
          <h3>Rent Checklist</h3>
        </div> -->
        
        <!-- Typography App Header Block -->
        <header class="checklist-header">
          <h1 class="main-title">Onboarding Checklist</h1>
          <p class="subtitle">Complete these requirements to start managing your portfolio.</p>
        </header>
  
        <!-- Checklist Structural Items Matrix -->
        <main class="items-container">
          <div 
            v-for="(item, index) in steps" 
            :key="index" 
            class="checklist-card"
            :class="{ 'card-active-focus': item.isActive }"
          >
            <!-- Left Icon Node Indicators -->
            <div class="badge-icon-wrapper" :class="item.status">
              <!-- Completed Status Check SVG Icon -->
              <svg v-if="item.status === 'completed'" viewBox="0 0 24 24" class="status-svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              
              <!-- Warning/Pending Action Attention SVG Icon -->
              <svg v-else viewBox="0 0 24 24" class="status-svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
  
            <!-- Central Descriptive Text Context Block -->
            <div class="card-body-text">
              <h2 class="item-title">{{ item.title }}</h2>
              <p class="item-desc">{{ item.description }}</p>
            </div>
  
            <!-- Right Interactive CTA Navigation Trigger Element -->
            <div v-if="item.isActive" class="action-wrapper">
              <button class="btn-primary" @click="handleActionClick(item.title)">
                View
              </button>
            </div>
          </div>
        </main>
  
        <!-- Bottom Progress Information Card Callout -->
        <footer class="progress-footer">
          <h3 class="footer-status-title">Almost there!</h3>
          <p class="footer-status-desc">
            Complete {{ pendingTasksCount }} more tasks to unlock full access.
          </p>
        </footer>
  
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'OnboardingChecklist',
    data() {
      return {
        steps: [
          {
            title: 'Profile Verification',
            description: 'Identity confirmed via KYC',
            status: 'completed',
            isActive: false
          },
          {
            title: 'Ownership Verification',
            description: 'Land titles validated',
            status: 'completed',
            isActive: false
          },
          {
            title: 'Tenancy Agreements',
            description: '2 units require signatures',
            status: 'attention',
            isActive: true
          },
          {
            title: 'No Pending Property Issues',
            description: 'No unresolved issues found',
            status: 'completed',
            isActive: false
          }
        ]
      };
    },
    computed: {
      // Computes tasks requiring attention or unfinished milestones
      pendingTasksCount() {
        return this.steps.filter(step => step.status !== 'completed').length + 1; 
      }
    },
    methods: {
      handleActionClick(title) {
        this.$emit('navigate-step', title);
      }
    }
  };
  </script>
  
  <style lang="scss" scoped>
  // SCSS Structural Layout Variables Configuration Matrix
  $color-bg-base: #fcfdfe;
  $color-text-title: #101623;
  $color-text-subtitle: #596173;
  $color-brand-blue: #0056ff;
  $color-status-green: #10b981;
  $color-status-orange: #f97316;
  $color-white: #ffffff;


  .pageTitle {
    width: 100%;
    margin-top: 40px;
    margin-bottom: 30px;
      h3 {
        font-family: Lato;
        font-weight: 600;
        font-size: 24px;
        line-height: 28px;
        letter-spacing: 0%;
        text-align: center;
        // color: 
      }
  }
  
  
  // Global Scoped Node Context Stylesheet setup
  .page-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background-color: #f8faff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    padding: 32px 18px;
    box-sizing: border-box;
  
    .card-wrapper {
      width: 100%;
      max-width: 480px;
      display: flex;
      flex-direction: column;
      background: #f8faff;
      box-shadow: 
        0 0 0 1px rgba(0, 0, 0, 0.05), 
        0 4px 12px rgba(0, 0, 0, 0.05), 
        0 12px 24px rgba(0, 0, 0, 0.05);
        padding: 15px;
        border-radius: 6px;
    }
  
    // Header Title Component
    .checklist-header {
      margin-bottom: 28px;
      padding: 0 4px;
  
      .main-title {
        font-size: 26px;
        font-weight: 700;
        color: $color-text-title;
        margin: 0 0 10px 0;
        letter-spacing: -0.4px;
      }
  
      .subtitle {
        font-size: 15px;
        line-height: 1.45;
        color: $color-text-subtitle;
        margin: 0;
      }
    }
  
    // Row Cards Container Array
    .items-container {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-bottom: 24px;
  
      .checklist-card {
        background-color: $color-white;
        border-radius: 18px;
        padding: 20px;
        display: flex;
        align-items: center;
        gap: 16px;
        box-shadow: 0 2px 10px rgba(0, 50, 120, 0.015), 0 10px 24px rgba(0, 50, 120, 0.025);
        border: 2px solid transparent;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        box-sizing: border-box;
  
        // Special active focused state box modifier styling
        &.card-active-focus {
          border-color: $color-brand-blue;
          box-shadow: 0 4px 14px rgba($color-brand-blue, 0.04), 0 14px 32px rgba($color-brand-blue, 0.06);
        }
  
        // Left Icon Nodes
        .badge-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          min-width: 32px;
          border-radius: 50%;
  
          &.completed {
            background-color: rgba($color-status-green, 0.1);
            .status-svg { fill: $color-status-green; }
          }
  
          &.attention {
            background-color: rgba($color-status-orange, 0.15);
            .status-svg { fill: $color-status-orange; }
          }
  
          .status-svg {
            width: 26px;
            height: 26px;
          }
        }
  
        // Middle Description Copy
        .card-body-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
  
          .item-title {
            font-size: 18px;
            font-weight: 700;
            color: $color-text-title;
            margin: 0;
            letter-spacing: -0.2px;
          }
  
          .item-desc {
            font-size: 13.5px;
            color: $color-text-subtitle;
            margin: 0;
            font-weight: 400;
          }
        }
  
        // Action Interactive Controls wrapper
        .action-wrapper {
          display: flex;
          align-items: center;
  
          .btn-primary {
            background-color: $color-brand-blue;
            color: $color-white;
            border: none;
            font-size: 14px;
            font-weight: 600;
            padding: 8px 24px;
            border-radius: 10px;
            cursor: pointer;
            transition: background-color 0.2s ease, transform 0.1s ease;
            box-shadow: 0 4px 10px rgba($color-brand-blue, 0.15);
  
            &:hover {
              background-color: darken($color-brand-blue, 5%);
            }
  
            &:active {
              transform: scale(0.97);
            }
          }
        }
      }
    }
  
    // Footer Dynamic Alert/Status container setup
    .progress-footer {
      border: 1.5px dashed #dce2ee;
      background-color: rgba(#ffffff, 0.4);
      border-radius: 16px;
      padding: 22px;
      text-align: center;
      display: flex;
      flex-direction: column;
      gap: 8px;
  
      .footer-status-title {
        font-size: 15px;
        font-weight: 600;
        color: #384052;
        margin: 0;
      }
  
      .footer-status-desc {
        font-size: 13.5px;
        color: #8c96a8;
        margin: 0;
        font-weight: 500;
      }
    }
  }
  
  // Media responsive viewports setup
  @media (max-width: 420px) {
    .page-container {
      padding: 20px 14px;
  
      .checklist-header {
        .main-title { font-size: 22px; }
        .subtitle { font-size: 13.5px; }
      }
  
      .items-container .checklist-card {
        padding: 16px 14px;
        gap: 12px;
  
        .card-body-text {
          .item-title { font-size: 16px; }
          .item-desc { font-size: 12.5px; }
        }
  
        .action-wrapper .btn-primary {
          padding: 7px 18px;
          font-size: 13px;
        }
      }
    }
  }
  </style>
  